import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {PriceElasticityService} from '../../../../services/price-elasticity.service';
import {UiBlockerService} from '../../../../services/ui-blocker.service';
import {ModelRunService} from '../../../../services/model-run.service';
import {ModelRun} from '../../../../models/model-run.model';
import {PriceElasticityReport} from '../../../../models/price-elasticity-report.model';
import {GenericConfirmationModalComponent} from '../../../../components/generic-confirmation-modal/generic-confirmation-modal.component';
import {SnackBarService} from '@app/components/snack-bar/snack-bar.service';
import {WebMessageService} from '@app/services/web-message.service';
import {AppConstantsService} from '@app/services/app-constants.service';
import { sanitizeFileName } from 'src/app/utils/object-utils';
import { saveAs } from 'file-saver';

const PRICE_ELASTICITY_REPORT_MSG_GROUP = 'price-elasticity-report';

@Component({
  selector: 'app-list-price-elasticity-report',
  templateUrl: './list-price-elasticity-report.component.html',
  styleUrls: ['./list-price-elasticity-report.component.scss']
})
export class ListPriceElasticityReportComponent implements OnInit, OnDestroy {

  modelRun: ModelRun;

  reports: Array<PriceElasticityReport>;

  subscriptions: Subscription;

  get projectId(): number {
    return this.modelRun.projectId;
  }

  get modelRunId(): string {
    return this.modelRun.id;
  }

  constructor(private route: ActivatedRoute,
              private router: Router,
              private modelRunService: ModelRunService,
              private uiBlockerService: UiBlockerService,
              private priceElasticityService: PriceElasticityService,
              private dialog: MatDialog,
              private snackBarService: SnackBarService,
              private ngZone: NgZone,
              private webMessageService: WebMessageService) {
  }

  ngOnInit(): void {
    this.subscriptions = new Subscription();
    /**
     * Angular nuances, when parent route(reports) param changes we need to watch it and reset the data accordingly.
     * I am not sure why it just doesn't re-render the route like in Ember.
     */
    this.subscriptions.add(this.route.parent.parent.params.subscribe(() => {
      this.uiBlockerService.block();
      this.reports = [];
      this.modelRun = this.modelRunService.activeModelRun;
      this.priceElasticityService.fetchAll(this.projectId, this.modelRunId).subscribe((reports: PriceElasticityReport[]) => {
        this.reports = reports;
        this.uiBlockerService.unblock();
        if (reports.length > 0) {
          this.webMessageService.consumeMessage(`${PRICE_ELASTICITY_REPORT_MSG_GROUP}-${this.modelRunId}`).subscribe((eventData) => {
            const event = JSON.parse(eventData);
            const updatedReport = this.priceElasticityService.refreshCachedModel(event.data);
            if (updatedReport) {
              this.reports.splice(this.reports.findIndex(it => it.id === updatedReport.id), 1, updatedReport);
            }
          });
        }
      });
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.webMessageService.close(`${PRICE_ELASTICITY_REPORT_MSG_GROUP}-${this.modelRunId}`);
  }

  createPriceElasticityRun(): void {
    this.router.navigate(['create'], {relativeTo: this.route.parent});
  }

  /**
   * Downloads optimization report file.
   * @see https://stackoverflow.com/questions/51960172/set-file-name-while-downloading-via-blob-in-angular-5
   */
  downloadReport(report: PriceElasticityReport, exportForSharePoint: boolean): void {
    this.uiBlockerService.block();
    if(!exportForSharePoint){
      this.priceElasticityService.generateReport(report).subscribe((response) => {
        const blob = new Blob([response], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        const sanitizedFileName = sanitizeFileName(report.name) + '.xlsx';
        saveAs(blob, sanitizedFileName);
        this.uiBlockerService.unblock();
      }, error => {
        this.uiBlockerService.unblock();
        this.snackBarService.openErrorSnackBar('Failed downloading report.');
      });
    }else{
      this.priceElasticityService.powerBIReportGeneration(report).subscribe((response) => {
        this.uiBlockerService.unblock();
        this.snackBarService.openSuccessSnackBar('Report exported to SharePoint successfully.');
      }, error => {
        this.uiBlockerService.unblock();
        const errorMessage = error.status == 423 ? 'Export failed because the file is currently open and cannot be replaced.' : 'Failed downloading report.';  
        this.snackBarService.openErrorSnackBar(errorMessage);
      });
    }
  }

  deleteReport(report: PriceElasticityReport): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '800px';
    dialogConfig.data = {
      header: 'Delete?',
      body: 'Are you sure you want to delete the report?',
      cancelButtonLabel: 'CANCEL',
      confirmButtonLabel: 'DELETE'
    };

    const dialogRef = this.dialog.open(GenericConfirmationModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(value => {
      if (value === 'DELETE') {
        this.uiBlockerService.block();
        this.priceElasticityService.delete(report).subscribe(() => {
          this.priceElasticityService.getAll(this.projectId, this.modelRunId).subscribe((reports: PriceElasticityReport[]) => {
            this.reports = reports;
            this.uiBlockerService.unblock();
          });
        }, error => {
          this.uiBlockerService.unblock();
        });
      }
    });
  }

  duplicate(report: PriceElasticityReport) {
    this.router.navigate(['create'], {
      relativeTo: this.route.parent,
      queryParams: {source: report.id}
    });
  }
}
